import { Alert, Box, Button, Snackbar, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const StatusSnackbar = ({ status, setStatus, autoHideDuration = 2000 }) => {
  const { getCardBackground, getTextColor, getGrayColor } = useInnovaTheme()

  const timerRef = useRef(null)
  const [notification, setNotification] = useState(null)
  const [progressKey, setProgressKey] = useState(0)

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === 'clickaway') return

      setStatus((prev) => ({
        ...prev,
        show: false,
      }))
    },
    [setStatus],
  )

  // Manage timer ourselves so hover does not pause dismissal
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (!status?.show) return

    setNotification({
      severity: status.severity || 'info',
      message: status.message,
    })

    setProgressKey((prev) => prev + 1)

    timerRef.current = setTimeout(() => {
      handleClose()
    }, autoHideDuration)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [status?.show, status?.message, status?.severity, autoHideDuration, handleClose])

  const severity = notification?.severity || 'info'

  // Severity colors remain distinct from the application accent color
  const severityColors = {
    success: '#4caf50',
    error: '#ef5350',
    warning: '#ffb74d',
    info: '#42a5f5',
  }

  const color = severityColors[severity] || '#42a5f5'

  const backgroundColor = getCardBackground()
  const textColor = getTextColor()
  const borderColor = getGrayColor(0.18)
  const trackColor = getGrayColor(0.1)

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={Boolean(status?.show)}
      onClose={handleClose}
      autoHideDuration={null}
      sx={{
        bottom: '24px !important',
        maxWidth: 'calc(100vw - 32px)',
      }}>
      <Alert
        onClose={handleClose}
        severity={severity}
        variant='standard'
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minWidth: { xs: 250, sm: 320 },
          maxWidth: 520,
          alignItems: 'center',
          borderRadius: 1.5,

          bgcolor: backgroundColor,
          color: textColor,
          border: '1px solid',
          borderColor,
          borderLeft: `3px solid ${color}`,

          boxShadow: '0 6px 24px rgba(0,0,0,0.25)',

          py: 0.75,
          px: 1.5,
          pb: 1.25,

          '& .MuiAlert-icon': {
            color,
            opacity: 1,
            alignItems: 'center',
          },

          '& .MuiAlert-message': {
            color: textColor,
            fontSize: '0.85rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },

          '& .MuiAlert-action': {
            alignItems: 'center',
            pt: 0,
            mr: -0.5,

            '& .MuiIconButton-root': {
              color: textColor,
              opacity: 0.65,
              padding: 0.5,

              '&:hover': {
                opacity: 1,
                bgcolor: getGrayColor(0.1),
              },
            },
          },
        }}>
        {notification?.message}

        {/* Countdown progress track */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: trackColor,
            overflow: 'hidden',
          }}>
          <Box
            key={progressKey}
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: color,
              opacity: 0.85,
              transformOrigin: 'left',
              animation: `statusCountdown ${autoHideDuration}ms linear forwards`,

              '@keyframes statusCountdown': {
                from: {
                  transform: 'scaleX(1)',
                },
                to: {
                  transform: 'scaleX(0)',
                },
              },
            }}
          />
        </Box>
      </Alert>
    </Snackbar>
  )
}

export const StatusSnackbarTest = () => {
  const [status, setStatus] = useState({
    show: false,
    severity: 'info',
    message: '',
  })

  const showStatus = (severity) => {
    setStatus({
      show: true,
      severity,
      message: `This is a ${severity} notification`,
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Status Snackbar Test
      </Typography>

      <Stack direction='row' spacing={2}>
        {['success', 'error', 'warning', 'info'].map((severity) => (
          <Button key={severity} variant='contained' color={severity} onClick={() => showStatus(severity)}>
            {severity}
          </Button>
        ))}
      </Stack>

      <StatusSnackbar status={status} setStatus={setStatus} autoHideDuration={4000} />
    </Box>
  )
}

export default StatusSnackbar
